import React from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import { Spinner } from "reactstrap";
import { AlertCircle, RefreshCw, ArrowRight } from "react-feather";
import { getActionRoute } from "./nextStepCopy";

export const DEFAULT_MAX_ROWS = 5;

export const getProgramStatusKey = (status) => {
  const rawStatus = (status || "live").toLowerCase();
  if (rawStatus === "live" || rawStatus === "active" || rawStatus === "published") return "live";
  if (rawStatus === "draft") return "draft";
  if (rawStatus === "closed" || rawStatus === "expired" || rawStatus === "disabled") return "closed";
  return rawStatus;
};

export const getProgramStatusLabel = (status, customLabel) => {
  if (customLabel) return customLabel;
  const rawStatus = (status || "").toLowerCase();
  if (rawStatus === "live" || rawStatus === "active" || rawStatus === "published") return "Live";
  if (rawStatus === "draft") return "Draft";
  if (rawStatus === "closed" || rawStatus === "expired" || rawStatus === "disabled") return "Closed";
  return status ? (status.charAt(0).toUpperCase() + status.slice(1)) : "Live";
};

export const selectBalancedPrograms = (programs, maxRows = DEFAULT_MAX_ROWS) => {
  const list = programs || [];
  if (list.length <= 1) return list;

  const live = [];
  const draft = [];
  const closed = [];
  const other = [];

  list.forEach((prog) => {
    const key = getProgramStatusKey(prog?.status);
    if (key === "live" || key === "active") live.push(prog);
    else if (key === "draft") draft.push(prog);
    else if (key === "closed") closed.push(prog);
    else other.push(prog);
  });

  const selected = [];
  const selectedIds = new Set();

  const addProg = (prog) => {
    if (prog && !selectedIds.has(prog.id ?? prog)) {
      selected.push(prog);
      selectedIds.add(prog.id ?? prog);
    }
  };

  // Aim for 1 Live, 1 Draft, 1 Closed
  if (live.length > 0) addProg(live[0]);
  if (draft.length > 0) addProg(draft[0]);
  if (closed.length > 0) addProg(closed[0]);

  // Backfill remaining slots up to maxRows
  const remainingPool = [
    ...live.slice(1),
    ...draft.slice(1),
    ...closed.slice(1),
    ...other
  ];

  for (const prog of remainingPool) {
    if (selected.length >= maxRows) break;
    addProg(prog);
  }

  for (const prog of list) {
    if (selected.length >= maxRows) break;
    addProg(prog);
  }

  return selected.slice(0, maxRows);
};

export const getVisiblePrograms = (programs, maxRows = DEFAULT_MAX_ROWS) => {
  const list = programs || [];
  // Display campaigns in the exact order returned by the API without additional sorting or filtering
  return list.slice(0, maxRows);
};

const YourPrograms = ({ programs, isLoading, error, onRetry, onExploreMore, maxRows = DEFAULT_MAX_ROWS }) => {
  const history = useHistory();

  const handleProgramClick = (prog) => {
    if (prog?.action) {
      const route = getActionRoute(prog.action);
      history.push(route);
    } else if (prog?.status === "draft") {
      history.push(`/brands/outreach-posts?editDraft=${prog.id}`);
    } else if (prog?.id) {
      history.push(`/brands/campaign-applications/${prog.id}`);
    } else {
      history.push("/brands/outreach-posts");
    }
  };

  const handleExploreMore = () => {
    if (onExploreMore) {
      onExploreMore();
    } else {
      history.push("/brands/outreach-posts");
    }
  };

  const programList = programs || [];
  const visiblePrograms = getVisiblePrograms(programList, maxRows);
  const showExploreMore = programList.length > 0;

  return (
    <div className="figma-split-card" id="brand-your-programs-card">
      <div className="figma-section-label mb-2">YOUR PROGRAMS</div>

      <div className="programs-container-list">
        {isLoading ? (
          <div className="d-flex justify-content-center align-items-center py-4" style={{ minHeight: "140px" }}>
            <Spinner color="primary" />
          </div>
        ) : error && programList.length === 0 ? (
          <div className="text-center py-3">
            <AlertCircle size={22} className="text-danger mb-50" />
            <p className="text-danger font-weight-bold mb-25 font-small-3">Failed to load programs</p>
            <p className="text-muted font-small-2 mb-75">
              {typeof error === "string" ? error : error?.message || "Could not retrieve your outreach programs."}
            </p>
            {onRetry && (
              <button
                type="button"
                className="btn btn-sm btn-outline-primary d-inline-flex align-items-center"
                onClick={onRetry}
              >
                <RefreshCw size={12} className="mr-50" />
                <span>Retry</span>
              </button>
            )}
          </div>
        ) : programList.length === 0 ? (
          <div className="text-center py-3">
            <p className="text-muted mb-1 font-small-3">No active outreach programs yet.</p>
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={() => history.push("/brands/outreach-posts?create=true")}
            >
              Create Outreach Post
            </button>
          </div>
        ) : (
          <>
            {visiblePrograms.map((prog, index) => {
              const statusKey = getProgramStatusKey(prog.status);
              const statusLabel = getProgramStatusLabel(prog.status, prog.status_label);
              const isLast = index === visiblePrograms.length - 1;

              const applicantCount = prog.applicant_count ?? prog.applicants_count ?? 0;
              const newCount = prog.new_applicant_count ?? 0;
              let metaString = prog.meta;
              if (!metaString) {
                if (statusKey === "draft") {
                  metaString = "Draft · not yet posted";
                } else if (statusKey === "closed") {
                  const spots = prog.capacity ? `${applicantCount} of ${prog.capacity} spots` : `${applicantCount} applicants`;
                  metaString = `${spots} · Closed`;
                } else {
                  const spots = prog.capacity ? `${applicantCount} of ${prog.capacity} spots` : `${applicantCount} applicants`;
                  metaString = newCount > 0 ? `${spots} · ${newCount} new` : spots;
                }
              }

              return (
                <div
                  className={`figma-program-row ${!isLast ? "has-divider" : ""}`}
                  key={`program_${prog.id || index}_${index}`}
                  onClick={() => handleProgramClick(prog)}
                  role="button"
                  tabIndex={0}
                >
                  <div className="program-title-line">
                    <span className="program-main-title">{prog.title}</span>
                    <span className={`status-pill-badge pill-${statusKey}`}>
                      {statusLabel}
                    </span>
                  </div>

                  <div className="program-subtitle-meta">
                    {metaString}
                  </div>
                </div>
              );
            })}

            {showExploreMore && (
              <div className="figma-explore-more-footer">
                <button
                  type="button"
                  className="btn btn-link p-0 figma-explore-more-link"
                  onClick={handleExploreMore}
                  id="explore-more-programs"
                >
                  <span className="explore-more-text">Explore More</span>
                  <ArrowRight size={14} className="ml-25 explore-more-arrow" />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

YourPrograms.propTypes = {
  programs: PropTypes.array,
  isLoading: PropTypes.bool,
  error: PropTypes.any,
  onRetry: PropTypes.func,
  onExploreMore: PropTypes.func,
  maxRows: PropTypes.number
};

YourPrograms.defaultProps = {
  programs: [],
  maxRows: DEFAULT_MAX_ROWS
};

export default YourPrograms;
